// Support Portal Route Configuration
import { lazy } from 'react';

// Lazy load Support Portal components
const SupportDashboard = lazy(
  () => import('../../portals/support/pages/dashboard/SupportDashboard')
);
const BroadcastManagement = lazy(
  () => import('../../portals/support/pages/broadcasts/BroadcastManagement')
);
const CreateBroadcast = lazy(
  () => import('../../portals/support/pages/broadcasts/CreateBroadcast')
);
const BroadcastDetail = lazy(
  () => import('../../portals/support/pages/broadcasts/BroadcastDetail')
);
const TicketManagement = lazy(() => import('../../portals/support/pages/tickets/TicketManagement'));
const TicketDetail = lazy(() => import('../../portals/support/pages/tickets/TicketDetail'));
const NotificationManagement = lazy(
  () => import('../../portals/support/pages/notifications/NotificationManagement')
);
const MyAssignedTickets = lazy(
  () => import('../../portals/support/pages/tickets/MyAssignedTickets')
);
const SupportAgents = lazy(() => import('../../portals/support/pages/agents/SupportAgents'));
const MyNotifications = lazy(() => import('../../portals/support/pages/notifications/MyNotifications'));
const NotificationDetail = lazy(() => import('../../portals/support/pages/notifications/NotificationDetail'));
const SettingsPage = lazy(() => import('../../portals/support/pages/settings/SettingsPage'));

/**
 * Support Portal Routes Configuration
 * ADMIN-ONLY PORTAL: For managing broadcasts, support tickets, and system notifications
 * Access is restricted to users with assigned_portal='admin'
 */
export const supportRoutes = {
  path: '/support',
  name: 'Support Center',
  description: 'Admin-only portal for managing communications and support',
  requiredRoles: [], // Portal access determined by assigned_portal='admin' field
  requiredPermissions: [],
  children: [
    {
      index: true,
      component: SupportDashboard,
      name: 'Dashboard',
      description: 'Support Center overview and statistics',
      requiredPermissions: [],
      meta: {
        title: 'Support Center Dashboard',
        breadcrumb: 'Dashboard',
        icon: 'dashboard',
      },
    },
    {
      path: 'broadcasts',
      component: BroadcastManagement,
      name: 'Broadcasts',
      description: 'Manage system-wide broadcasts and announcements',
      requiredPermissions: [],
      meta: {
        title: 'Broadcast Management',
        breadcrumb: 'Broadcasts',
        icon: 'campaign',
      },
    },
    {
      path: 'broadcasts/create',
      component: CreateBroadcast,
      name: 'Create Broadcast',
      description: 'Create new broadcast with targeting options',
      requiredPermissions: [],
      meta: {
        title: 'Create Broadcast',
        breadcrumb: 'Create',
        hideInNavigation: true,
      },
    },
    {
      path: 'broadcasts/:broadcastId',
      component: BroadcastDetail,
      name: 'Broadcast Detail',
      description: 'View broadcast details and statistics',
      requiredPermissions: [],
      meta: {
        title: 'Broadcast Details',
        breadcrumb: 'Details',
        hideInNavigation: true,
      },
    },
    {
      path: 'tickets',
      component: TicketManagement,
      name: 'Support Tickets',
      description: 'Manage all user support tickets',
      requiredPermissions: [],
      meta: {
        title: 'Support Ticket Management',
        breadcrumb: 'Tickets',
        icon: 'confirmation_number',
      },
    },
    {
      path: 'tickets/:ticketId',
      component: TicketDetail,
      name: 'Ticket Detail',
      description: 'View and respond to support ticket',
      requiredPermissions: [],
      meta: {
        title: 'Ticket Details',
        breadcrumb: 'Details',
        hideInNavigation: true,
      },
    },
    {
      path: 'notifications',
      component: NotificationManagement,
      name: 'Notification Management',
      description: 'Monitor delivery, manage preferences, configure channels and templates',
      requiredPermissions: [],
      meta: {
        title: 'Notification Management',
        breadcrumb: 'Notifications',
        icon: 'notifications',
      },
    },
    {
      path: 'notifications/:notificationId',
      component: NotificationDetail,
      name: 'Notification Detail',
      description: 'View notification details and responses',
      requiredPermissions: [],
      meta: {
        title: 'Notification Detail',
        breadcrumb: 'Detail',
        hideInNavigation: true,
      },
    },
    {
      path: 'my-notifications/:notificationId',
      component: NotificationDetail,
      name: 'My Notification Detail',
      description: 'View notification details',
      requiredPermissions: [],
      meta: {
        title: 'Notification Detail',
        breadcrumb: 'Detail',
        hideInNavigation: true,
      },
    },
    {
      path: 'my-tickets',
      component: MyAssignedTickets,
      name: 'My Assigned Tickets',
      description: 'View and manage tickets assigned to you',
      requiredPermissions: [],
      meta: {
        title: 'My Assigned Tickets',
        breadcrumb: 'My Tickets',
        icon: 'assignment_ind',
      },
    },
    {
      path: 'agents',
      component: SupportAgents,
      name: 'Support Agents',
      description: 'Manage support team and agent workload',
      requiredPermissions: [],
      meta: {
        title: 'Support Agents',
        breadcrumb: 'Agents',
        icon: 'support_agent',
      },
    },
    {
      path: 'my-notifications',
      component: MyNotifications,
      name: 'My Notifications',
      description: 'View your personal notifications',
      requiredPermissions: [],
      meta: {
        title: 'My Notifications',
        breadcrumb: 'My Notifications',
        icon: 'notifications',
      },
    },
    {
      path: 'settings',
      component: SettingsPage,
      name: 'Settings',
      description: 'Configure Support Center preferences and email settings',
      requiredPermissions: [],
      meta: {
        title: 'Support Settings',
        breadcrumb: 'Settings',
        icon: 'settings',
      },
    },
  ],
};

export default supportRoutes;
